/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {DestroyRef, Component, Inject, Input, inject, signal} from '@angular/core';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import template from './creative-set-edit-page.component.html';

import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {advertiserFromNavigation, advertiserNavigationParams, folioFromNavigation} from '../../components/util/navigation-context';
import {getMatchingFee, simplifyFee} from '../line-item/line-item-fee-utils';
import {summarizeCreativeFormatFees} from '../line-item/line-item-edit-calculations';
import {postFillGenerateFormatFees} from '../creative/creative-commons';
import {formatPercentage} from '../common/filters/format-percentage';
import {formatAdnStat} from '../common/filters/format-adn-stat';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {CreativeApiService} from '../creative/creative-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {SearchApiService} from '../common/controller/search-api.service';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {deferSignalStateWrite} from '../common/util/template-state';
import {creativeSetBreadcrumbData, CreativeSetInfoApiService} from './creative-set-info-api.service';
import {CreativeSetApiService, CreativeSetModel} from './creative-set-api.service';
import {AdvertisingSharedModule} from '../advertising/advertising-shared.module';
import {LineItemModule} from '../line-item/line-item.module';

@Component({
  standalone: true,
  imports: [AdvertisingSharedModule, LineItemModule],
  selector: 'adn-creative-set-edit-page',
  template
})
export class CreativeSetEditPageComponent extends SignalAdminResourceEditPageBase<CreativeSetModel> {
  private readonly listLifetime = inject(DestroyRef);
  @Input() folioFromParam: any;
  @Input() advertiserFromParam: any;

  currencies: string[] = [];
  readonly folioResults = signal<any[]>([]);
  readonly uiFees = signal<{fees: any[]}>({fees: []});
  readonly teamsWarning = signal(false);

  readonly creativeListHost = signal<any>({
    isCreativeSet: true,
    isNew: true,
    model: {},
    items: [],
    bulkSelection: {},
    bulkSelectSize: 0
  });

  private formatFeesCache: any[] = [];
  private numCreativeCache = -1;
  private creativeListInitialized = false;

  constructor(
    @Inject(LocalNetworkProfile) private readonly networkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly searchApi: SearchApiService,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    private readonly creativeSetInfoApi: CreativeSetInfoApiService,
    private readonly listBinding: SignalResourceListBindingService,
    private readonly $state: StateService,
    private readonly uiRouter: UIRouter,
    private readonly appLocale: AppLocaleService,
    private readonly navigationHistory: NavigationHistoryService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(inject(CreativeSetApiService), 'creativeSet');
    this.currencies = networkProfile.get('availableCurrencies') || [];
    this.creativeListHost.update((host) => ({
      ...host,
      perms: LocalUserPermissions.getAllVisPermissions()
    }));
  }

  ngOnChanges(): void {
    this.syncCreativeListHost();
  }

  folioSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchApi.search({q: searchElement.search, types: 'Folio'}).subscribe((data) => {
      deferSignalStateWrite(() => this.folioResults.set(data.searchResults || []));
    });
  };

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  onFolioChange(): void {
    this.markExternalFormDirty();
  }

  formatFee(fee: any): string {
    if (fee.type === 'PERCENTAGE') {
      return formatPercentage(fee.percentage / 100, 2, undefined, this.appLocale.getLocaleId());
    }
    return formatAdnStat(fee.monetary, undefined, undefined, undefined, this.networkProfile.get('defaultCurrency'), this.appLocale.getLocaleId());
  }

  newBulkCreative(): void {
    this.$state.go('app.adv.creatives.bulkupload', _.assign({}, advertiserNavigationParams(this.editingModel.advertiser), {
      creativeSetId: this.editingModel.id,
      creativeSetName: this.editingModel.name
    }));
  }

  protected override initialiseModel(model: CreativeSetModel): CreativeSetModel {
    const initialised = super.initialiseModel(model);
    initialised.renderOption = initialised.renderOption || null;

    if (this.isNewCreativeSet()) {
      const folio = this.folioFromParam || folioFromNavigation(this.navigationHistory, this.uiRouter.globals.params);
      if (folio?.id) {
        const folioCopy = _.cloneDeep(folio);
        initialised.folio = folioCopy;
        this.folioResults.set([folioCopy]);
      }
      const advertiser = this.advertiserFromParam || advertiserFromNavigation(this.navigationHistory, this.uiRouter.globals.params);
      if (advertiser?.id) {
        const advertiserCopy = _.cloneDeep(advertiser);
        initialised.advertiser = advertiserCopy;
      }
    }

    if (this.isNew && !initialised.startDate) {
      const networkTimezone = this.networkProfile.get('timeZone') || 'utc';
      initialised.startDate = moment().tz(networkTimezone).startOf('day').toDate();
    }

    if (initialised.startDate) {
      initialised.modStartDate = initialised.startDate;
    }
    if (initialised.endDate) {
      initialised.modEndDate = initialised.endDate;
    }

    initialised.userState = initialised.userState || 'APPROVED';
    return initialised;
  }

  protected override afterEditingModelReset(model: CreativeSetModel): void {
    this.syncCreativeListHost();
    this.refreshCreativeList();
    this.getUiFees();

    // New CS ids are client-generated; CreativeSetInfo only exists after save.
    if (!this.isNewCreativeSet() && model.id) {
      this.creativeSetInfoApi.get(model.id).subscribe({
        next: (cs) => {
          deferSignalStateWrite(() => {
            const breadcrumbs = creativeSetBreadcrumbData(this.editingModel, cs, {
              advertiser: this.advertiserFromParam,
              folio: this.folioFromParam
            });
            if (_.get(breadcrumbs, ['folio', 'id'])) {
              this.editingModel.folio = breadcrumbs.folio;
            }
            if (_.get(breadcrumbs, ['advertiser', 'id'])) {
              this.editingModel.advertiser = breadcrumbs.advertiser;
            }
            this.state.updateRelatedData('creativeSetBreadcrumbs', breadcrumbs);
            this.creativeListHost.update((host) => ({...host, model: this.editingModel}));
          });
        }
      });
    }
  }

  protected override afterSave(saved: CreativeSetModel): void {
    this.teamsWarning.set((_.get(this.editingModel, 'teams') || []).length !== (_.get(saved, 'teams') || []).length);
    saved.renderOption = saved.renderOption || null;
    saved.userState = saved.userState || 'APPROVED';
  }

  protected override prepareForSubmit(model: CreativeSetModel): void {
    if (this.isNew) {
      this.localNetworkInfo.increment('creativeSetCount');
    }
    model.startDate = _.isDate(model.modStartDate) ? model.modStartDate : null;
    model.endDate = _.isDate(model.modEndDate) ? model.modEndDate : null;
  }

  private isNewCreativeSet(): boolean {
    return !!this.isNew;
  }

  private syncCreativeListHost(): void {
    this.creativeListHost.update((host) => ({
      ...host,
      isCreativeSet: true,
      isNew: !!this.isNew,
      model: this.editingModel,
      perms: this.LocalUserPermissions.getAllVisPermissions(),
      newBulkCreative: () => this.newBulkCreative(),
      updateRelData: host.updateRelData || (() => undefined)
    }));
  }

  private refreshCreativeList(): void {
    if (this.isNew || !this.model?.id) {
      this.creativeListInitialized = false;
      return;
    }
    const host = this.creativeListHost();
    if (this.creativeListInitialized && host.makeQuery) {
      host.makeQuery();
      return;
    }
    this.creativeListInitialized = true;
    const creativeListHost = this.listBinding.create(this.Creative, 'fullCreativesForCreativeSet', this.listLifetime, {
      baseTable: 'creative',
      moreParams: {creativeSet: this.model.id},
      concise: true,
      pageSize: 101,
      afterFill: () => {
        const currentHost = this.creativeListHost();
        if (_.isFunction(currentHost.updateRelData)) {
          currentHost.updateRelData(currentHost.items, 'creatives');
        }
        const data = postFillGenerateFormatFees(currentHost);
        const refCurrency = this.getUiFees(data.creativeFormatFees, data.validItems.length);
        _.forEach(currentHost.items, (item: any) => {
          const fee = simplifyFee(this.networkProfile, refCurrency, item.creativeFormatFee);
          if (fee && fee.monetary.amount > 0) {
            item.simplifiedCreativeFormatFee = fee;
          }
        });
      }
    }, host);
    this.creativeListHost.set(creativeListHost);
  }

  private getUiFees(creativeFormatFees?: any[], numCreatives?: number): any {
    const referenceCurrency = _.cloneDeep(_.get(this, ['editingModel', 'billingCurrency']));
    const fees: any[] = [];

    if (_.get(this.editingModel, ['creativeTagTechFeeCpm', 'type'])) {
      const cpmTechFee = _.cloneDeep(this.editingModel.creativeTagTechFeeCpm);
      cpmTechFee.name = cpmTechFee.name || this.networkProfile.getNetworkTranslations('creativeTagFee');
      cpmTechFee.id = cpmTechFee.id || 'creative-tag-tech-fee';
      fees.push(cpmTechFee);
    }
    if (creativeFormatFees) {
      this.numCreativeCache = numCreatives;
      this.formatFeesCache.length = 0;
      _.forEach(creativeFormatFees, (f) => {
        const fee = simplifyFee(this.networkProfile, referenceCurrency, f);
        if (fee) {
          this.formatFeesCache.push(fee);
        }
      });
    }
    if (this.formatFeesCache.length) {
      const ff = summarizeCreativeFormatFees(
        this.formatFeesCache,
        this.numCreativeCache,
        referenceCurrency,
        this.networkProfile.getNetworkTranslations('creativeFormatFee')
      );
      if (ff) {
        fees.push(ff);
      }
    }
    const standardiseFees = (fees: any[]) => {
      _.forEach(fees, (theFee) => {
        if (theFee.type === 'MULTI_CURRENCY_MONETARY') {
          const matchingFee = getMatchingFee(theFee.multiCurrencyMonetaryAmounts, referenceCurrency);
          if (matchingFee) {
            theFee.monetary = matchingFee;
          }
        }
      });
    };
    standardiseFees(fees);
    if (creativeFormatFees) {
      standardiseFees(creativeFormatFees);
    }
    this.uiFees.set({fees});
    return referenceCurrency;
  }
}
